import React, { useEffect, useState } from "react";
import * as Styled from "../../../CompanyForm.styled";
import * as MUIStyled from "../../../MUIStyled";
import { CustomTextField } from "../../elements/TextField/TextField.js";
import CheckboxWithLabel from "../../elements/CheckBoxWithLabel/CheckBoxWithLabel";

function ContactCard({
    contact,
    contactTypesPrimary,
    onSetFormDataChange,
    signatoryPrimarySelectedId,
    setSignatoryPrimarySelectedId,
    onSignatoryPrimaryChange,
    payrollPrimarySelectedId,
    setPayrollPrimarySelectedId,
    onPayrollPrimaryChange,
    accountsPrimarySelectedId,
    setAccountsPrimarySelectedId,
    onAccountsPrimaryChange,
    hrPrimarySelectedId,
    setHrPrimarySelectedId,
    onHrPrimaryChange,
    onRemoveContact,
    errors,
}) {
    const { id } = contact;

    const [primaryTypes, setPrimaryTypes] = useState([]);
    const [secondaryTypes, setSecondaryTypes] = useState([]);

    const [isChecked, setIsChecked] = useState({
        id: id,
        signatoryPrimary: false,
        signatorySecondary: false,
        payrollPrimary: false,
        payrollSecondary: false,
        accountsPrimary: false,
        accountsSecondary: false,
        hrPrimary: false,
        hrSecondary: false,
    });

    const [signatoryPrimaryDisabled, setSignatoryPrimaryDisabled] =
        useState(false);
    const [payrollPrimaryDisabled, setPayrollPrimaryDisabled] = useState(false);
    const [accountsPrimaryDisabled, setAccountsPrimaryDisabled] =
        useState(false);
    const [hrPrimaryDisabled, setHrPrimaryDisabled] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        lastname: "",
        email: "",
        phone: "",
        title: "",
        primaryTypes: [],
        types: [],
    });

    // fill the form with data from getContactsAPI
    useEffect(() => {
        if (contact && contact.id) {
            setFormData({
                id: contact.id,
                name: contact.name || "",
                lastname: contact.lastname || "",
                email: contact.email || "",
                phone: contact.phone || "",
                title: contact.title || "",
                primaryTypes: contact.primaryTypes || [],
                types: contact.secondaryTypes || [],
            });
        }
        // eslint-disable-next-line
    }, []);

    // Pass Data to parent
    useEffect(() => {
        onSetFormDataChange(formData);
        // eslint-disable-next-line
    }, [formData]);

    // Update formData when primaryTypes or secondaryTypes change
    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            primaryTypes,
            types: secondaryTypes,
        }));
    }, [primaryTypes, secondaryTypes]);

    // make other primary checkbox disabled - only one primary contact is allowed
    useEffect(() => {
        if (
            signatoryPrimarySelectedId !== null &&
            signatoryPrimarySelectedId !== id
        ) {
            setIsChecked((prev) => ({
                ...prev,
                signatoryPrimary: false,
            }));
            setSignatoryPrimaryDisabled(true);
        } else {
            setSignatoryPrimaryDisabled(false);
        }

        if (
            payrollPrimarySelectedId !== null &&
            payrollPrimarySelectedId !== id
        ) {
            setIsChecked((prev) => ({
                ...prev,
                payrollPrimary: false,
            }));
            setPayrollPrimaryDisabled(true);
        } else {
            setPayrollPrimaryDisabled(false);
        }

        if (
            accountsPrimarySelectedId !== null &&
            accountsPrimarySelectedId !== id
        ) {
            setIsChecked((prev) => ({
                ...prev,
                accountsPrimary: false,
            }));
            setAccountsPrimaryDisabled(true);
        } else {
            setAccountsPrimaryDisabled(false);
        }

        if (hrPrimarySelectedId !== null && hrPrimarySelectedId !== id) {
            setIsChecked((prev) => ({
                ...prev,
                hrPrimary: false,
            }));
            setHrPrimaryDisabled(true);
        } else {
            setHrPrimaryDisabled(false);
        }
    }, [
        signatoryPrimarySelectedId,
        payrollPrimarySelectedId,
        accountsPrimarySelectedId,
        hrPrimarySelectedId,
        id,
    ]);

    // Load checked values from ContactsAPI
    const syncIsCheckedWithContact = (contact) => {
        setIsChecked({
            id: contact.id,
            signatoryPrimary: contact.primaryTypes.includes(
                contactTypesPrimary.SIGNATORY
            ),
            signatorySecondary: contact.types.includes(
                contactTypesPrimary.SIGNATORY
            ),

            payrollPrimary: contact.primaryTypes.includes(
                contactTypesPrimary.PAYROLL
            ),
            payrollSecondary: contact.types.includes(
                contactTypesPrimary.PAYROLL
            ),

            accountsPrimary: contact.primaryTypes.includes(
                contactTypesPrimary.ACCOUNTS
            ),
            accountsSecondary: contact.types.includes(
                contactTypesPrimary.ACCOUNTS
            ),

            hrPrimary: contact.primaryTypes.includes(contactTypesPrimary.HR),
            hrSecondary: contact.types.includes(contactTypesPrimary.HR),
        });
    };

    useEffect(() => {
        if (contact) {
            setPrimaryTypes(contact.primaryTypes || []);
            setSecondaryTypes(contact.types || []);
            syncIsCheckedWithContact(contact);
        }
        // eslint-disable-next-line
    }, [contact]);

    // Load checked values from ContactsAPIcheck, make other primary checkbox disabled
    useEffect(() => {
        if (
            contact &&
            isChecked.id === contact.id &&
            isChecked.signatoryPrimary
        ) {
            setSignatoryPrimarySelectedId(contact.id);
        }
        if (
            contact &&
            isChecked.id === contact.id &&
            isChecked.payrollPrimary
        ) {
            setPayrollPrimarySelectedId(contact.id);
        }
        if (
            contact &&
            isChecked.id === contact.id &&
            isChecked.accountsPrimary
        ) {
            setAccountsPrimarySelectedId(contact.id);
        }
        if (contact && isChecked.id === contact.id && isChecked.hrPrimary) {
            setHrPrimarySelectedId(contact.id);
        }
    }, [
        contact,
        isChecked,
        setSignatoryPrimarySelectedId,
        setPayrollPrimarySelectedId,
        setAccountsPrimarySelectedId,
        setHrPrimarySelectedId,
    ]);

    const handleChange = (event) => {
        const { name, checked } = event.target;

        if (name === "signatoryPrimary") {
            onSignatoryPrimaryChange(id, checked);
        }
        if (name === "payrollPrimary") {
            onPayrollPrimaryChange(id, checked);
        }
        if (name === "accountsPrimary") {
            onAccountsPrimaryChange(id, checked);
        }
        if (name === "hrPrimary") {
            onHrPrimaryChange(id, checked);
        }

        const nameToTypeMap = {
            signatoryPrimary: {
                id: contactTypesPrimary.SIGNATORY,
                isPrimary: true,
            },
            payrollPrimary: {
                id: contactTypesPrimary.PAYROLL,
                isPrimary: true,
            },
            accountsPrimary: {
                id: contactTypesPrimary.ACCOUNTS,
                isPrimary: true,
            },
            hrPrimary: { id: contactTypesPrimary.HR, isPrimary: true },

            signatorySecondary: {
                id: contactTypesPrimary.SIGNATORY,
                isPrimary: false,
            },
            payrollSecondary: {
                id: contactTypesPrimary.PAYROLL,
                isPrimary: false,
            },
            accountsSecondary: {
                id: contactTypesPrimary.ACCOUNTS,
                isPrimary: false,
            },
            hrSecondary: { id: contactTypesPrimary.HR, isPrimary: false },
        };

        const { id: typeId, isPrimary } = nameToTypeMap[name];

        const updateTypes = (setter, currentArray) => {
            setter(
                checked
                    ? [...new Set([...currentArray, typeId])] // add
                    : currentArray.filter((id) => id !== typeId) // remove
            );
        };

        if (isPrimary) {
            updateTypes(setPrimaryTypes, primaryTypes);
        } else {
            updateTypes(setSecondaryTypes, secondaryTypes);
        }

        setIsChecked((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleFieldsChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    return (
        <>
            <Styled.ContactFormWrapper>
                <Styled.ContactFormFieldsWrapper>
                    <Styled.ContactCardTitle fontSize="18px">
                        Contact
                    </Styled.ContactCardTitle>
                    <Styled.ContactFormRow>
                        <CustomTextField
                            fullWidth
                            id={String(id)}
                            name="name"
                            variant="outlined"
                            label="First name *"
                            type="text"
                            value={formData.name}
                            onChange={handleFieldsChange}
                            sx={{
                                "&::placeholder": {
                                    fontWeight: "bold",
                                    fontSize: 16,
                                },
                            }}
                            error={Boolean(errors.name)}
                            helperText={errors.name || ""}
                        />
                        <CustomTextField
                            fullWidth
                            id={String(id)}
                            name="lastname"
                            label="Last name *"
                            variant="outlined"
                            type="text"
                            value={formData.lastname}
                            onChange={handleFieldsChange}
                            sx={{
                                "&::placeholder": {
                                    fontWeight: "bold",
                                    fontSize: 16,
                                },
                            }}
                            error={Boolean(errors.lastname)}
                            helperText={errors.lastname || ""}
                        />
                    </Styled.ContactFormRow>
                    <Styled.ContactFormRow>
                        <CustomTextField
                            fullWidth
                            id={String(id)}
                            name="email"
                            label="Email *"
                            variant="outlined"
                            type="email"
                            value={formData.email}
                            onChange={handleFieldsChange}
                            sx={{
                                "&::placeholder": {
                                    fontWeight: "bold",
                                    fontSize: 16,
                                },
                            }}
                            error={Boolean(errors.email)}
                            helperText={errors.email || ""}
                        />
                    </Styled.ContactFormRow>
                    <Styled.ContactFormRow>
                        <CustomTextField
                            fullWidth
                            id={String(id)}
                            name="phone"
                            label="Phone *"
                            variant="outlined"
                            type="text"
                            value={formData.phone}
                            onChange={handleFieldsChange}
                            sx={{
                                "&::placeholder": {
                                    fontWeight: "bold",
                                    fontSize: 16,
                                },
                            }}
                            error={Boolean(errors.phone)}
                            helperText={errors.phone || ""}
                        />
                        <CustomTextField
                            fullWidth
                            id={String(id)}
                            name="title"
                            type="text"
                            label="Job Title *"
                            variant="outlined"
                            value={formData.title}
                            onChange={handleFieldsChange}
                            sx={{
                                "&::placeholder": {
                                    fontWeight: "bold",
                                    fontSize: 16,
                                },
                            }}
                            error={Boolean(errors.title)}
                            helperText={errors.title || ""}
                        />
                    </Styled.ContactFormRow>
                    {id <= 10 && (
                        <Styled.ContactRemoveLink
                            onClick={() => onRemoveContact(id)}>
                            Remove
                        </Styled.ContactRemoveLink>
                    )}
                </Styled.ContactFormFieldsWrapper>

                <Styled.ContactCheckboxWrapper>
                    <Styled.ContactCardTitle fontSize="18px">
                        Select contact types*
                        <MUIStyled.LightTooltip
                            title={
                                <>
                                    <div>
                                        <b>Authorised signatory:</b> Person
                                        authorised to sign novations on the
                                        company's behalf.
                                    </div>
                                    <div>
                                        <b>Payroll:</b> This contact will
                                        receive the novated lease payroll
                                        advices in order to set up the salary
                                        deductions in your payroll system.
                                    </div>
                                    <div>
                                        <b>Accounts:</b> This contact will
                                        receive the novated lease invoices for
                                        processing.
                                    </div>
                                    <div>
                                        <b>HR:</b> This contact may receive
                                        educational content and be engaged by
                                        our employer support team to explore
                                        opportunities for implementing the
                                        benefits across the organisation.
                                    </div>
                                </>
                            }>
                            <MUIStyled.CustomIconButton size="small">
                                <Styled.StyledHelpIcon />
                            </MUIStyled.CustomIconButton>
                        </MUIStyled.LightTooltip>
                    </Styled.ContactCardTitle>
                    <CheckboxWithLabel
                        checked={isChecked.signatoryPrimary}
                        onChange={handleChange}
                        name="signatoryPrimary"
                        size="small"
                        disabled={signatoryPrimaryDisabled}
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    color: signatoryPrimaryDisabled
                                        ? "#00000042!important"
                                        : "inherit",
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Authorised signatory (Primary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.signatorySecondary}
                        onChange={handleChange}
                        name="signatorySecondary"
                        size="small"
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Authorised signatory (Secondary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.payrollPrimary}
                        onChange={handleChange}
                        name="payrollPrimary"
                        size="small"
                        disabled={payrollPrimaryDisabled}
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    color: payrollPrimaryDisabled
                                        ? "#00000042!important"
                                        : "inherit",
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Payroll (Primary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.payrollSecondary}
                        onChange={handleChange}
                        name="payrollSecondary"
                        size="small"
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Payroll (Secondary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.accountsPrimary}
                        onChange={handleChange}
                        name="accountsPrimary"
                        size="small"
                        disabled={accountsPrimaryDisabled}
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    color: accountsPrimaryDisabled
                                        ? "#00000042!important"
                                        : "inherit",
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Accounts (Primary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.accountsSecondary}
                        onChange={handleChange}
                        name="accountsSecondary"
                        size="small"
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                Accounts (Secondary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.hrPrimary}
                        onChange={handleChange}
                        name="hrPrimary"
                        size="small"
                        disabled={hrPrimaryDisabled}
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    color: hrPrimaryDisabled
                                        ? "#00000042!important"
                                        : "inherit",
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                HR (Primary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                    <CheckboxWithLabel
                        checked={isChecked.hrSecondary}
                        onChange={handleChange}
                        name="hrSecondary"
                        size="small"
                        inputProps={{ "aria-label": "controlled" }}
                        sx={{ width: "100%", textAlign: "left" }}
                        label={
                            <MUIStyled.CustomTypography
                                sx={{
                                    fontSize: "16px!important",
                                    padding: "0px!important",
                                }}>
                                HR (Secondary)
                            </MUIStyled.CustomTypography>
                        }
                    />
                </Styled.ContactCheckboxWrapper>
            </Styled.ContactFormWrapper>

            <Styled.ContactFormSeparator />
        </>
    );
}

export default ContactCard;
